/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import externalIDApi from '@redux/apis/id'
import { genAPIDomain } from '@utils/api'
import { getTimeZoneOffset, getTimeZoneDateInstance } from '@utils/time'

const initCommonState: Redux.CommonState = {
  region: '',
  apiDomain: '',
  externalID: null,
  roleName: null,
  c1UserTimeZone: '',
  c1UserTimeZoneOffsetByHr: '+00',
  hasAccessPermission: null,
}

function setExternalIDAction(
  state: typeof initCommonState,
  action: PayloadAction<string>
) {
  state.externalID = action.payload
}

function setRegionAction(
  state: typeof initCommonState,
  action: PayloadAction<string>
) {
  const region = action.payload

  state.region = region
  state.apiDomain = genAPIDomain(region)
}

function setTimeZoneAction(
  state: typeof initCommonState,
  action: PayloadAction<string>
) {
  state.c1UserTimeZone = action.payload
  state.c1UserTimeZoneOffsetByHr = action.payload
    ? getTimeZoneOffset(action.payload)
    : state.c1UserTimeZoneOffsetByHr
}

function setRoleNameAction(
  state: typeof initCommonState,
  action: PayloadAction<C1CRedux.C1RoleName>
) {
  state.roleName = action.payload
}

function setHasAccessPermissionAction(
  state: typeof initCommonState,
  action: PayloadAction<boolean>
) {
  state.hasAccessPermission = action.payload
}

function resetCommonAction() {
  return initCommonState
}

export const commonSlice = createSlice({
  name: 'common',
  initialState: initCommonState,
  reducers: {
    setExternalID: setExternalIDAction,
    setRegion: setRegionAction,
    setTimeZone: setTimeZoneAction,
    setRoleName: setRoleNameAction,
    setHasAccessPermission: setHasAccessPermissionAction,
    resetCommon: resetCommonAction,
  },
  extraReducers: builder => {
    builder.addMatcher(
      externalIDApi.endpoints.getExternalID.matchFulfilled,
      (state, action) => {
        state.externalID = action.payload.externalID
      }
    )
  },
})

export const selectSelf = (state: Redux.RootState) => state.common

export const regionSelector = createSelector(
  selectSelf,
  common => common.region
)
export const apiDomainSelector = createSelector(
  selectSelf,
  common => common.apiDomain
)

export const externalIDSelector = createSelector(
  selectSelf,
  common => common.externalID
)

export const roleNameSelector = createSelector(
  selectSelf,
  common => common.roleName
)

export const hasAccessPermissionSelector = createSelector(
  selectSelf,
  common => common.hasAccessPermission
)

export const c1UserTimeZoneSelector = createSelector(selectSelf, common => ({
  // These states will be updated at the same moment.
  c1UserTimeZone: common.c1UserTimeZone,
  getC1UserTzDateInstance: getTimeZoneDateInstance(common.c1UserTimeZone),
  c1UserTimeZoneOffsetByHr: common.c1UserTimeZoneOffsetByHr,
}))

export const {
  setExternalID,
  setRegion,
  setTimeZone,
  setRoleName,
  setHasAccessPermission,
  resetCommon,
} = commonSlice.actions

export const REGION_ACTION_TYPE = setRegion.toString()
export const TIME_ZONE_ACTION_TYPE = setTimeZone.toString()
export const EXTERNAL_ID_ACTION_TYPE = setExternalID.toString()
export const ROLE_NAME_ACTION_TYPE = setRoleName.toString()
export const HAS_ACCESS_PERMISSION_TYPE = setHasAccessPermission.toString()

export default commonSlice.reducer
